<div class="g-menu text-text-primary w-[288px]">
  <p class="g-menu-label">{{ 'filters.operation' | translate }}</p>
  <div class="px-1 pb-1">
    <app-select
      [items]="operationItems()"
      [value]="selectedOperation()"
      (valueChange)="selectOperation($any($event))" />
  </div>

  @if (showValueInput()) {
    <div class="g-menu-divider"></div>
    <p class="g-menu-label">{{ 'filters.value' | translate }}</p>

    <div class="px-1 pb-1">
      @if (isTextFilter()) {
        <input
          type="text"
          class="form-input"
          [value]="getTextValue()"
          (input)="updateTextValue($any($event.target).value)"
          [placeholder]="'filters.enter_value' | translate" />
      }

      @if (isNumberFilter()) {
        <input
          type="number"
          class="form-input"
          [value]="getNumberValue()"
          (input)="updateNumberValue(+$any($event.target).value)"
          [placeholder]="'filters.enter_number' | translate" />
      }

      @if (isDateFilter() && selectedOperation() !== FilterOperation.IS_RELATIVE) {
        <input
          type="date"
          class="form-input"
          [value]="getDateValue()"
          (input)="updateDateValue($any($event.target).value)" />
      }

      @if (isDateFilter() && selectedOperation() === FilterOperation.IS_RELATIVE) {
        <app-select
          [items]="relativeDateItems()"
          [value]="getRelativeDate() ?? null"
          [placeholder]="'filters.select_relative_date' | translate"
          (valueChange)="updateRelativeDate($any($event))" />
      }

      @if (isDurationFilter()) {
        <input
          type="text"
          class="form-input"
          [value]="getDurationValue()"
          (input)="updateDurationValue($any($event.target).value)"
          [placeholder]="'filters.enter_duration' | translate" />
      }

      @if (isSelectFilter() && showMultiSelect()) {
        <app-multi-autocomplete
          [ngModel]="selectValues()"
          (ngModelChange)="updateConditionValuesFromAutoComplete($event)"
          [options]="availableValues()"
          [allowCustomValues]="false"
          [placeholder]="'filters.select_values' | translate"
          [separators]="autoCompleteSeparators" />
      }

      @if (isTagsFilter()) {
        <app-multi-autocomplete
          [ngModel]="tagValues()"
          (ngModelChange)="updateConditionValuesFromAutoComplete($event, 'tagNames')"
          [options]="availableValues()"
          [allowCustomValues]="true"
          [placeholder]="'filters.enter_tags' | translate"
          [separators]="autoCompleteSeparators" />
      }

      @if (isLabelsFilter()) {
        @if (isLabelHasValueInput() && !isLabelHasMultipleValues()) {
          @if (isEnumLabelFilter()) {
            <!-- Enum label: constrain to declared variants -->
            <app-select
              [items]="availableValues()"
              [value]="getLabelValue() || null"
              [placeholder]="'filters.select_value' | translate"
              (valueChange)="updateLabelSingleValue($any($event) ?? '')" />
          } @else {
            <!-- Free-text label: suggest what the dataspace already uses, if anything. -->
            <app-autocomplete
              [options]="availableValues()"
              [ngModel]="getLabelValue()"
              (ngModelChange)="updateLabelSingleValue($event ?? '')"
              [placeholder]="'filters.enter_label_value' | translate" />
          }
        }

        @if (isLabelHasValueInput() && isLabelHasMultipleValues()) {
          <app-multi-autocomplete
            [ngModel]="labelValues()"
            (ngModelChange)="updateLabelMultipleValues($event)"
            [options]="isEnumLabelFilter() ? availableValues() : emptyOptions"
            [allowCustomValues]="!isEnumLabelFilter()"
            [placeholder]="'filters.enter_label_values' | translate"
            [separators]="autoCompleteSeparators" />
        }
      }

      @if (isSelectFilter() && !showMultiSelect()) {
        <app-select
          [items]="availableValues()"
          [value]="selectValues()[0] || null"
          [placeholder]="'filters.select_value' | translate"
          (valueChange)="setSelectValue($any($event) ?? '')" />
      }

      @if (isUidListFilter() || (isUidFilter() && isUidFilterMultiSelect())) {
        <app-entity-auto-complete
          [formControl]="uidListFormControl"
          [value]="uidListFormControl.value"
          [entityType]="getUidEntityType()"
          [ownerEntityType]="FILTER_PRESET_SURFACE"
          [id]="filteredField()"
          [isArray]="true"
          class="w-full" />
      }

      @if (isUidFilter() && !isUidFilterMultiSelect()) {
        <app-entity-auto-complete
          [formControl]="uidFormControl"
          [value]="uidFormControl.value"
          [entityType]="getUidEntityType()"
          [ownerEntityType]="FILTER_PRESET_SURFACE"
          [id]="filteredField()"
          [isArray]="false"
          class="w-full" />
      }
    </div>
  }

  @if (showTwoValueInputs()) {
    <div class="g-menu-divider"></div>
    <p class="g-menu-label">{{ 'filters.range' | translate }}</p>

    @if (isDateFilter()) {
      <!-- Two native date inputs cannot fit side by side in the popup width, so they are stacked. -->
      <div class="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1.5 px-1 pb-1">
        <label for="filter-date-min" class="text-text-secondary text-sm font-medium">{{
          'filters.from' | translate
        }}</label>
        <input
          id="filter-date-min"
          type="date"
          class="form-input min-w-0"
          [value]="getDateMinValue()"
          (input)="updateDateMinValue($any($event.target).value)" />
        <label for="filter-date-max" class="text-text-secondary text-sm font-medium">{{
          'filters.to' | translate
        }}</label>
        <input
          id="filter-date-max"
          type="date"
          class="form-input min-w-0"
          [value]="getDateMaxValue()"
          (input)="updateDateMaxValue($any($event.target).value)" />
      </div>
    } @else {
      <div class="flex items-center gap-1.5 px-1 pb-1">
        @if (isNumberFilter()) {
          <input
            type="number"
            class="form-input flex-1"
            [value]="getNumberMinValue()"
            (input)="updateNumberMinValue(+$any($event.target).value)"
            [placeholder]="'filters.min' | translate" />
          <span class="text-text-tertiary">—</span>
          <input
            type="number"
            class="form-input flex-1"
            [value]="getNumberMaxValue()"
            (input)="updateNumberMaxValue(+$any($event.target).value)"
            [placeholder]="'filters.max' | translate" />
        }

        @if (isDurationFilter()) {
          <input
            type="text"
            class="form-input flex-1"
            [value]="getDurationMinValue()"
            (input)="updateDurationMinValue($any($event.target).value)"
            [placeholder]="'filters.min_duration' | translate" />
          <span class="text-text-tertiary">—</span>
          <input
            type="text"
            class="form-input flex-1"
            [value]="getDurationMaxValue()"
            (input)="updateDurationMaxValue($any($event.target).value)"
            [placeholder]="'filters.max_duration' | translate" />
        }
      </div>
    }
  }

  <div class="g-menu-footer">
    <app-button
      class="shrink-0"
      (clickEvent)="removeFilter()"
      type="secondary"
      [icon]="ICONS.trash"
      [label]="'common.delete' | translate" />
    <app-button
      class="flex-1"
      (clickEvent)="applyFilter()"
      type="primary"
      [fullWidth]="true"
      [icon]="ICONS.check"
      [label]="'common.save' | translate" />
  </div>
</div>
